import { useEffect, useState } from 'react';
import { supabase } from '../../supabase';

/**
 * Web2 mirror of `apps/mobile/lib/parent/childSummary.ts`'s
 * `getChildHighlights` helper. Reads `video_tags` filtered to the
 * child + visibility=PARENT.
 *
 * RLS does the real gating — the `video_tags_parent_read` policy
 * (migration 00028) requires:
 *   - caller's users.role = 'PARENT'
 *   - tag.visibility = 'PARENT'
 *   - parent_children link between caller and the tag's player_id
 *
 * The MEDIA consent check is at the UI layer (in Highlights.tsx) —
 * even when RLS allows the row, we hide the gallery if the parent
 * hasn't ticked the MEDIA box.
 */

export interface ChildHighlight {
  id: string;
  clipUrl: string;
  platform: 'YOUTUBE' | 'VEO' | 'HUDL' | 'INTERNAL' | string;
  tsIn: number;            // seconds from clip start
  tsOut: number;
  cornerId: string | null;
  coachNote: string | null;
  isHighlight: boolean;
  createdAt: string;
}

interface State {
  data: ChildHighlight[];
  loading: boolean;
  error: string | null;
}

export function useChildHighlights(playerId: string | null): State {
  const [state, setState] = useState<State>({ data: [], loading: true, error: null });

  useEffect(() => {
    let cancelled = false;
    if (!playerId) {
      setState({ data: [], loading: false, error: null });
      return;
    }
    setState({ data: [], loading: true, error: null });

    (async () => {
      const { data, error } = await supabase
        .from('video_tags')
        .select('id, clip_url, platform, ts_in, ts_out, corner_id, coach_note, is_highlight, created_at, visibility')
        .eq('player_id', playerId)
        .eq('visibility', 'PARENT')
        .order('created_at', { ascending: false })
        .limit(40);
      if (cancelled) return;
      if (error) {
        // Soft fail — empty list keeps the page rendering; the surface
        // shows the "nothing yet" empty state.
        // eslint-disable-next-line no-console
        console.warn('[useChildHighlights] read failed', error.message);
        setState({ data: [], loading: false, error: error.message });
        return;
      }
      const rows: ChildHighlight[] = (data ?? []).map((t: any) => ({
        id: String(t.id),
        clipUrl: String(t.clip_url ?? ''),
        platform: String(t.platform ?? 'INTERNAL'),
        tsIn: Number(t.ts_in ?? 0),
        tsOut: Number(t.ts_out ?? 0),
        cornerId: (t.corner_id as string | null) ?? null,
        coachNote: (t.coach_note as string | null) ?? null,
        isHighlight: t.is_highlight === true,
        createdAt: String(t.created_at ?? ''),
      }));
      setState({ data: rows, loading: false, error: null });
    })();

    return () => { cancelled = true; };
  }, [playerId]);

  return state;
}
